import {
  CLUSTER_MARKER_RADIUS_PX,
  IBox,
  IExportKeyMember,
  IExportLabelFeature,
  IExportScenePin,
  KEY_BADGE_RADIUS_PX,
} from './exportLabelTypes'

// Clear space kept between any two markers once clustering settles.
const CLUSTER_GAP_PX = 4

export interface IExportPinCluster {
  id: string
  centre: { x: number; y: number }
  count: number
  pinKeys: string[]
  icons: IBox[]
  // Newest first, as their labels would have been placed.
  members: IExportKeyMember[]
}

interface INode {
  x: number
  y: number
  count: number
  pinKeys: string[]
  icons: IBox[]
  featureIds: string[]
  members: IExportKeyMember[]
}

// What a node covers on the page: a lone pin its own icon, a counter its disk plus the letter tag
// on its rim, each at its own scale, with the key on or off, so the switch never changes which
// pins group.
const radiusOf = (node: INode, markScale: number, diskScale: number): number =>
  isCounter(node)
    ? CLUSTER_MARKER_RADIUS_PX * diskScale + KEY_BADGE_RADIUS_PX * markScale
    : Math.max(node.icons[0].width, node.icons[0].height) / 2

// Several Workflows in one place, or a pin whose names have no label of their own.
const isCounter = (node: INode): boolean =>
  node.count > 1 || (!node.featureIds.length && node.members.length > 0)

const absorb = (into: INode, other: INode): void => {
  const total = into.count + other.count
  into.x = (into.x * into.count + other.x * other.count) / total
  into.y = (into.y * into.count + other.y * other.count) / total
  into.count = total
  into.pinKeys.push(...other.pinKeys)
  into.icons.push(...other.icons)
  into.featureIds.push(...other.featureIds)
  into.members.push(...other.members)
}

const compareMembers = (a: IExportKeyMember, b: IExportKeyMember): number =>
  a.sortKey - b.sortKey || a.text.localeCompare(b.text)

// Collision-driven: any two markers that would touch merge, at their Workflow-weighted centre, and
// the pass repeats until none touch. A stack or a shared pin is a counter even alone, so "several
// Workflows here" has one look. Larger disks merge more pins, so drawn disks never overlap.
export function clusterNearbyPins(
  features: IExportLabelFeature[],
  unlabelledPins: IExportScenePin[],
  markScale = 1,
  diskScale = markScale,
): {
  singles: IExportLabelFeature[]
  clusters: IExportPinCluster[]
} {
  const node = (
    key: string,
    icon: IBox,
    count: number,
    members: IExportKeyMember[],
    featureId?: string,
  ): INode => ({
    x: icon.x + icon.width / 2,
    y: icon.y + icon.height / 2,
    count,
    pinKeys: [key],
    icons: [icon],
    featureIds: featureId ? [featureId] : [],
    members: [...members],
  })
  let nodes: INode[] = [
    ...features
      .filter(f => f.kind === 'workflowPin' && f.icon && f.pinKey)
      .map(f =>
        node(
          f.pinKey,
          f.icon,
          f.pinCount ?? 1,
          [{ text: f.text, sortKey: f.sortKey }],
          f.id,
        ),
      ),
    ...unlabelledPins.map(pin =>
      node(pin.key, pin.icon, pin.count, pin.members),
    ),
  ]

  let isMerging = true
  while (isMerging) {
    isMerging = false
    nodes.sort(
      (a, b) =>
        b.count - a.count ||
        a.y - b.y ||
        a.x - b.x ||
        a.pinKeys[0].localeCompare(b.pinKeys[0]),
    )
    const absorbed = new Set<INode>()
    nodes.forEach(a => {
      if (absorbed.has(a)) return
      nodes.forEach(b => {
        if (b === a || absorbed.has(b)) return
        const reach =
          radiusOf(a, markScale, diskScale) +
          radiusOf(b, markScale, diskScale) +
          CLUSTER_GAP_PX
        if (Math.hypot(a.x - b.x, a.y - b.y) < reach) {
          absorb(a, b)
          absorbed.add(b)
          isMerging = true
        }
      })
    })
    nodes = nodes.filter(n => !absorbed.has(n))
  }

  const clusteredFeatures = new Set<string>()
  const clusters: IExportPinCluster[] = []
  nodes.filter(isCounter).forEach(n => {
    n.featureIds.forEach(id => clusteredFeatures.add(id))
    clusters.push({
      id: `cluster:${[...n.pinKeys].sort()[0]}`,
      centre: { x: n.x, y: n.y },
      count: n.count,
      pinKeys: n.pinKeys,
      icons: n.icons,
      members: [...n.members].sort(compareMembers),
    })
  })

  return {
    singles: features.filter(f => !clusteredFeatures.has(f.id)),
    clusters,
  }
}
